import React, { Component, ErrorInfo, ReactNode } from "react";
import { Button, Result } from "antd";
import { posthog } from "../lib/posthog";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary that captures React errors and reports them to PostHog
 */
export class PostHogErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Capture error details for debugging
    console.error("Error caught by PostHog Error Boundary:", error, errorInfo);

    // Update state for UI
    this.setState({
      error,
      errorInfo,
    });

    // Report to PostHog using enhanced error tracking
    posthog.capture("$exception", {
      error_message: error.message,
      error_name: error.name,
      error_stack: error.stack,
      error_source: "react_error_boundary",
      error_severity: "critical",
      component_stack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href,
      pathname: window.location.pathname,
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
    });

    // Also capture a custom event for React-specific tracking
    posthog.capture("react_error_boundary_triggered", {
      error_message: error.message,
      error_name: error.name,
      component_stack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  }

  handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      // Render error fallback UI
      return (
        <Result
          status="error"
          title="Something went wrong"
          subTitle="The application has encountered an unexpected error."
          extra={[
            <Button type="primary" key="reload" onClick={this.handleReload}>
              Reload page
            </Button>,
          ]}
        />
      );
    }

    // If no error, render children normally
    return this.props.children;
  }
}
